import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  apiGetSportive, apiGetCycles, apiGetMentals, apiGetSports,
  apiGetCyclePhases, apiGetDashboardSummary, apiGetSuivisForCoach,
} from '../services/api';
import StaticConcentricPie from '../components/StaticConcentricPie';
import InfoButton from '../components/InfoButton';
import PhaseBadge from '../components/PhaseBadge';
import useTeams from '../hooks/useTeams';
import { filterLastNDays } from '../utils/dateRange';
import {
  PHASE_API_MAP, PHASE_COLORS_MAP, PHASE, PHASE_CONTRAST, MENTAL, SPORT, UI,
  CYCLE_PIE_COLORS, MENTAL_PIE_COLORS, SPORT_PIE_COLORS,
} from '../constants/colors';
import './CoachSportive.css';
import { parseISODate, todayISO } from '../utils/formDate';
import { MOOD_EMOJI } from '../constants/labels';

const PHASE_LABEL_MAP = { regles: 'Règles', folliculaire: 'Folliculaire', ovulation: 'Ovulation', luteale: 'Lutéale' };
// Clés du moteur de cycle → slugs de style (map partagée, voir constants/colors.js).
const PHASE_MAP_COACH = PHASE_API_MAP;
// Décryptage physiologique par phase (i) — clés définies dans constants/graphInfoTexts.js
const PHASE_INFO_KEY = {
  regles: 'coachPhaseRegles',
  folliculaire: 'coachPhaseFolliculaire',
  ovulation: 'coachPhaseOvulation',
  luteale: 'coachPhaseLuteale',
};

const ageFrom = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
};

const fmtDate = (iso) => {
  const d = iso ? parseISODate(iso) : null;
  if (!d) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const BackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const CoachSportive = () => {
  const { id } = useParams();
  const sid = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAuth();
  const { getTeamsForMember } = useTeams(user?.id);

  const seeded = location.state || {};
  const [profile, setProfile] = useState(seeded.profile || null);
  const [suivi, setSuivi] = useState(seeded.suivi || null);
  const [cycles, setCycles] = useState([]);
  const [mentals, setMentals] = useState([]);
  const [sports, setSports] = useState([]);
  const [phases, setPhases] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sid || !user?.id) { setLoading(false); return; }
    const token = getToken();
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiGetSportive(sid, token).catch(() => null),
      apiGetCycles(sid, token).catch(() => []),
      apiGetMentals(sid, token).catch(() => []),
      apiGetSports(sid, token).catch(() => []),
      apiGetCyclePhases(sid, token).catch(() => []),
      apiGetDashboardSummary(sid, token).catch(() => null),
      apiGetSuivisForCoach(user.id, token).catch(() => []),
    ]).then(([prof, c, m, s, ph, d, suivis]) => {
      if (cancelled) return;
      if (prof) setProfile(prof);
      setCycles(c || []);
      setMentals(m || []);
      setSports(s || []);
      setPhases(ph || []);
      setDash(d);
      const match = (suivis || []).find(x => x.id_sportive === sid);
      if (match) setSuivi(match);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sid, user?.id, getToken]);

  const today = todayISO();

  // ── Dérivés cycle ──
  const lastPhaseRaw = useMemo(
    () => [...phases].sort((a, b) => b.date.localeCompare(a.date))[0],
    [phases]
  );
  // Phase *actuelle* seulement : une fenêtre de partage close avant aujourd'hui
  // s'arrête sur un jour passé, qui ne dit rien de la phase en cours.
  const currentPhaseRaw = lastPhaseRaw?.date === today ? lastPhaseRaw : null;
  const phaseKey = currentPhaseRaw ? (PHASE_MAP_COACH[currentPhaseRaw.phase] || currentPhaseRaw.phase.toLowerCase()) : null;
  const phaseLabel = phaseKey ? (PHASE_LABEL_MAP[phaseKey] || phaseKey) : '—';
  const phaseColor = phaseKey ? (PHASE_COLORS_MAP[phaseKey] || UI.surfaceEmpty) : UI.surfaceEmpty;
  const phaseTextColor = phaseColor === PHASE.folliculaire ? PHASE_CONTRAST.folliculaireDark : phaseColor;

  // ── Dérivés mental (30 j) ──
  const mentalsRange = useMemo(
    () => filterLastNDays([...mentals].sort((a, b) => String(a.date).localeCompare(String(b.date))), 30, (m) => m.date),
    [mentals]
  );
  const avgMental = (field) => {
    const vals = mentalsRange.map(m => m[field]).filter(v => v != null);
    if (!vals.length) return null;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10);
  };
  const productivityPct = avgMental('productivity');
  const concentrationPct = avgMental('concentration');
  const selfEsteemPct = avgMental('self_esteem');
  const lastMental = useMemo(() => [...mentals].sort((a, b) => b.date.localeCompare(a.date))[0], [mentals]);
  const moodEmoji = lastMental?.mood ? (MOOD_EMOJI[lastMental.mood] || '😶') : null;

  // ── Dérivés sport (30 j) ──
  const sportsRange = useMemo(
    () => filterLastNDays([...sports].sort((a, b) => String(a.date).localeCompare(String(b.date))), 30, (s) => s.date),
    [sports]
  );
  const avgOf = (list, field, round = true) => {
    const vals = list.map(x => x[field]).filter(v => v != null);
    if (!vals.length) return null;
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    return round ? Math.round(mean) : mean;
  };
  const avgRestHR = avgOf(sportsRange, 'rest_heart_rate');
  const avgEffortHR = avgOf(sportsRange, 'effort_heart_rate');
  const avgRpeNum = avgOf(sportsRange, 'rpe', false);
  const avgRpe = avgRpeNum != null ? avgRpeNum.toFixed(1) : null;
  const avgRpeColor = avgRpeNum != null ? (avgRpeNum <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense) : null;

  const lastSport = useMemo(() => [...sports].sort((a, b) => b.date.localeCompare(a.date))[0], [sports]);
  const lastCycle = useMemo(() => [...cycles].sort((a, b) => b.date.localeCompare(a.date))[0], [cycles]);

  // ── Partage ──
  // Suivi introuvable (liste en échec, lien rompu) : tout est présenté verrouillé. Le
  // serveur refuse de toute façon, mais l'écran ne doit pas promettre un partage.
  const canCycle = !!suivi?.partage_cycle;
  const canMental = !!suivi?.partage_mental;
  const canSport = !!suivi?.partage_sport;
  const canDetail = !!suivi?.partage_detail;

  const fullName = profile ? `${profile.prenom} ${profile.nom}` : `Sportive #${sid}`;
  const initials = profile ? `${profile.prenom?.[0] ?? ''}${profile.nom?.[0] ?? ''}` : 'S';
  const age = ageFrom(profile?.date_naissance);
  const memberTeams = getTeamsForMember(sid);

  const infoChips = [
    age != null && { label: 'Âge', value: `${age} ans` },
    profile?.sport && { label: 'Sport', value: profile.sport },
  ].filter(Boolean);

  const notes = [
    canCycle && lastCycle?.notes && { label: 'Cycle', text: lastCycle.notes, color: PHASE.regles },
    canMental && lastMental?.notes && { label: 'Mental', text: lastMental.notes, color: MENTAL.primary },
    canSport && lastSport?.notes && { label: 'Sport', text: lastSport.notes, color: SPORT.primary },
  ].filter(Boolean);

  // Bande hebdo cycle (7 derniers jours)
  const weekDays = useMemo(() => {
    const byDate = Object.fromEntries(cycles.map(c => [String(c.date), c]));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - 6 + i);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const entry = byDate[dateStr];
      const dotColor = entry?.flow ? PHASE.regles
        : entry?.ovulation_test ? PHASE.ovulation
        : entry && !entry.is_rest_day ? PHASE.folliculaire
        : null;
      return { dayLabel: ['D', 'L', 'M', 'M', 'J', 'V', 'S'][d.getDay()], dayNum: d.getDate(), isToday: dateStr === today, dotColor };
    });
  }, [cycles, today]);

  return (
    <div className="cs">
      {/* ── En-tête avec retour ── */}
      <div className="cs__topbar">
        <button className="cs__back" onClick={() => navigate('/coach')} aria-label="Retour">
          <BackIcon />
        </button>
        <span className="cs__topbar-title">Fiche sportive</span>
      </div>

      {/* ── Bandeau identité ── */}
      <div className="cs__hero">
        <div className="cs__avatar" style={{ background: MENTAL.accent }}>{initials || 'S'}</div>
        <div className="cs__hero-info">
          <h1 className="cs__name">{fullName}</h1>
          <span className="cs__phase">
            {profile?.sport && <span className="cs__sport">{profile.sport}</span>}
            {canCycle && <PhaseBadge phase={currentPhaseRaw?.phase} />}
          </span>
        </div>
        {moodEmoji && <span className="cs__hero-mood">{moodEmoji}</span>}
      </div>

      {loading && (
        <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0' }}>Chargement…</p>
      )}

      {/* ── Chips profil ── */}
      {infoChips.length > 0 && (
        <div className="cs__chips">
          {infoChips.map(c => (
            <div key={c.label} className="cs__chip">
              <span className="cs__chip-label">{c.label}</span>
              <span className="cs__chip-value">{c.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Période de suivi ── */}
      {suivi && (
        <div className="cs__suivi-band">
          <span>
            Données partagées&nbsp;:{' '}
            {!suivi.partage_debut && !suivi.partage_fin
              ? "tout l'historique"
              : `${suivi.partage_debut ? fmtDate(suivi.partage_debut) : '…'} → ${suivi.partage_fin ? fmtDate(suivi.partage_fin) : "aujourd'hui"}`}
          </span>
          {suivi.statut && suivi.statut !== 'active' && (
            <span className="cs__suivi-status">{suivi.statut === 'en_attente' ? 'En attente' : suivi.statut}</span>
          )}
        </div>
      )}

      {/* ── Répartition du mois (camembert concentrique) ── */}
      <div className="cs__card">
        <div className="cs__card-head">
          <h2 className="cs__card-title">Répartition du mois</h2>
          {dash?.reliability_pct != null && (
            <span className="cs__reliability">{dash.reliability_pct}% fiable</span>
          )}
        </div>
        <div className="cs__pie">
          <StaticConcentricPie
            cycleSlices={dash?.cycle_pie?.map(s => ({ ...s, color: CYCLE_PIE_COLORS[s.label] ?? s.color }))}
            mentalSlices={dash?.mental_pie?.map(s => ({ ...s, color: MENTAL_PIE_COLORS[s.label] ?? s.color }))}
            sportSlices={dash?.sport_pie?.map(s => ({ ...s, color: SPORT_PIE_COLORS[s.label] ?? s.color }))}
            cycleDayPct={dash?.cycle_day_pct ?? null}
            cycleDay={dash?.cycle_day ?? null}
            cycleDays={dash?.cycle_length || 28}
            size={210}
          />
        </div>
        <div className="cs__legend">
          <span className="cs__legend-item"><span className="cs__legend-dot" style={{ background: PHASE.ovulation }} />Cycle</span>
          <span className="cs__legend-item"><span className="cs__legend-dot" style={{ background: MENTAL.concentration }} />Mental</span>
          <span className="cs__legend-item"><span className="cs__legend-dot" style={{ background: SPORT.fcEffort }} />Sport</span>
        </div>
        {dash?.is_estimated && <p className="cs__estimated">Estimation — s'affine avec un suivi régulier</p>}
      </div>

      {/* ── Cycle ── */}
      <div className="cs__card">
        <h2 className="cs__card-title cs__card-title--cycle">Cycle menstruel</h2>
        {!canCycle ? (
          <p className="cs__locked">La sportive ne partage pas ses données de cycle.</p>
        ) : cycles.length === 0 ? (
          <p className="cs__empty">Aucune donnée de cycle enregistrée.</p>
        ) : (
          <>
            <p className="cs__phase-line">
              Phase actuelle : <strong style={{ color: phaseTextColor }}>{phaseLabel}</strong>
              {phaseKey && <InfoButton infoKey={PHASE_INFO_KEY[phaseKey]} />}
            </p>
            <div className="cs__week">
              {weekDays.map((d, i) => (
                <div key={i} className="cs__week-day">
                  <span className="cs__week-label">{d.dayLabel}</span>
                  <span className={`cs__week-num${d.isToday ? ' cs__week-num--today' : ''}`}>{d.dayNum}</span>
                  <span className="cs__week-dot" style={{ background: d.dotColor || 'transparent' }} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Mental ── */}
      <div className="cs__card">
        <h2 className="cs__card-title cs__card-title--mental">État mental</h2>
        {!canMental ? (
          <p className="cs__locked">La sportive ne partage pas ses données mentales.</p>
        ) : mentalsRange.length === 0 ? (
          <p className="cs__empty">Aucune donnée mentale sur 30 jours.</p>
        ) : (
          <div className="cs__metrics">
            {[
              { label: 'Productivité', value: productivityPct, color: MENTAL.productivite },
              { label: 'Concentration', value: concentrationPct, color: MENTAL.concentration },
              { label: 'Estime de soi', value: selfEsteemPct, color: MENTAL.estime },
            ].map(m => (
              <div key={m.label} className="cs__metric">
                <div className="cs__metric-bar-track">
                  <div className="cs__metric-bar" style={{ height: `${m.value ?? 0}%`, background: m.color }} />
                </div>
                <span className="cs__metric-value" style={{ color: m.color }}>{m.value != null ? `${m.value}%` : '—'}</span>
                <span className="cs__metric-label">{m.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Sport ── */}
      <div className="cs__card">
        <h2 className="cs__card-title cs__card-title--sport">Activité sportive</h2>
        {!canSport ? (
          <p className="cs__locked">La sportive ne partage pas ses données sportives.</p>
        ) : sportsRange.length === 0 ? (
          <p className="cs__empty">Aucune donnée sportive sur 30 jours.</p>
        ) : (
          <div className="cs__sport-stats">
            <div className="cs__sport-stat">
              <span className="cs__sport-stat-label">FC repos</span>
              <span className="cs__sport-stat-value" style={{ color: SPORT.fcRepos }}>{avgRestHR != null ? `${avgRestHR}` : '—'}<small> bpm</small></span>
            </div>
            <div className="cs__sport-stat">
              <span className="cs__sport-stat-label">FC effort</span>
              <span className="cs__sport-stat-value" style={{ color: SPORT.fcEffort }}>{avgEffortHR != null ? `${avgEffortHR}` : '—'}<small> bpm</small></span>
            </div>
            <div className="cs__sport-stat">
              <span className="cs__sport-stat-label">RPE moyen</span>
              <span className="cs__sport-stat-value" style={avgRpeColor ? { color: avgRpeColor } : undefined}>{avgRpe != null ? avgRpe : '—'}<small>/10</small></span>
            </div>
          </div>
        )}
      </div>

      {/* ── Notes / ressentis ── */}
      <div className="cs__card">
        <h2 className="cs__card-title">Notes récentes</h2>
        {!canDetail ? (
          <p className="cs__locked">La sportive n'a pas activé le partage détaillé : ses notes et ressentis restent privés.</p>
        ) : notes.length === 0 ? (
          <p className="cs__empty">Aucune note récente.</p>
        ) : (
          <div className="cs__notes">
            {notes.map(n => (
              <div key={n.label} className="cs__note">
                <span className="cs__note-tag" style={{ background: n.color + '22', color: n.color }}>{n.label}</span>
                <p className="cs__note-text">{n.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Équipes ── */}
      {memberTeams.length > 0 && (
        <div className="cs__teams">
          {memberTeams.map(t => (
            <span key={t.id} className="cs__team-tag" style={{ background: t.color + '22', color: t.color, borderColor: t.color + '55' }}>
              {t.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoachSportive;
